import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import IconButton from "@mui/material/IconButton"
import type { SxProps } from "@mui/material/styles"
import MapIcon from "@mui/icons-material/Place"
import Popover from "@mui/material/Popover"
import PopupState, { bindTrigger, bindPopover } from "material-ui-popup-state"
import { useCallback, useEffect, useState } from "react"
import { GOOGLE_MAPS_API_KEY } from "@erinfo/env/src/other"

const width = 350
const height = 250
const loadTimeout = 8000 // a cross-origin iframe reports nothing on failure, so a stalled load is the only signal we get

interface IProps {
  lat: number | string
  long: number | string
  iconButtonSx?: SxProps
}

export const PhotoLocationMap = (props: IProps) => (
  <PopupState variant="popover" popupId="photo-location-popover">
    {(popupState) => (
      <>
        <IconButton {...bindTrigger(popupState)} sx={props.iconButtonSx}>
          <MapIcon color="primary" />
        </IconButton>

        <Popover
          {...bindPopover(popupState)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          {GOOGLE_MAPS_API_KEY ? (
            <MapFrame lat={props.lat} long={props.long} />
          ) : (
            <Fallback lat={props.lat} long={props.long} />
          )}
        </Popover>
      </>
    )}
  </PopupState>
)

const MapFrame = (props: Pick<IProps, "lat" | "long">) => {
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const handleLoad = useCallback(() => {
    setLoaded(true)
    setFailed(false) // a load this late still wins over the stall fallback
  }, [])
  const handleError = useCallback(() => setFailed(true), [])

  useEffect(() => {
    if (loaded) return
    const timeoutId = setTimeout(() => setFailed(true), loadTimeout)
    return () => clearTimeout(timeoutId)
  }, [loaded])

  return (
    <Box sx={{ width, height, position: "relative" }}>
      {/* stays mounted while the fallback shows, so a slow map can still replace it */}
      <iframe
        title="Photo location map"
        width={width}
        height={height}
        style={{ border: 0, display: "block" }}
        referrerPolicy="no-referrer-when-downgrade"
        onLoad={handleLoad}
        onError={handleError}
        src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_API_KEY}&q=${props.lat},${props.long}`}
      />
      {failed && (
        <Fallback overlay lat={props.lat} long={props.long} />
      )}
    </Box>
  )
}

const Fallback = (
  props: Pick<IProps, "lat" | "long"> & { overlay?: boolean },
) => (
  <Box
    sx={{
      width,
      height,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      p: 2,
      textAlign: "center",
      ...(props.overlay && {
        position: "absolute",
        inset: 0,
        bgcolor: "background.paper",
      }),
    }}
    role="alert"
  >
    <Typography>The map could not be loaded.</Typography>
    <Typography sx={{ opacity: 0.7 }}>
      {`Photo location: ${props.lat}, ${props.long}`}
    </Typography>
  </Box>
)
